import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import profileImg from "../../assets/images/profile.svg";
import "./developer-console.scss";
import { getDeveloperDashboard, getDeveloperGames } from "../../services/developerService";
import { getCurrentUserId, getCurrentUserProfile } from "../../services/dashboardService";
import { computeDeveloperStats } from "./developerUtils";
import DeveloperLayout from "./DeveloperLayout";
import DeveloperOverview from "./DeveloperOverview";
import DeveloperReports from "./DeveloperReports";
import GameList from "./game-list";
import CreateGameModal from "./create-game-modal";

function DeveloperDashboard() {
  const navigate = useNavigate();
  const { developerId: routeDeveloperId } = useParams();
  const isAdminView = Boolean(routeDeveloperId);
  const [dashboardData, setDashboardData] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [developerId, setDeveloperId] = useState(null);
  const [userAvatar, setUserAvatar] = useState(profileImg);
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [viewingAs, setViewingAs] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const profile = await getCurrentUserProfile();

        if (profile && profile.role !== "developer" && !(profile.role === "admin" && isAdminView)) {
          setError(
            `Access denied. User "${profile.email || "unknown"}" has role "${profile.role || "playtester"}". Only developers can access this dashboard.`
          );
          return;
        }

        if (!profile) {
          setError("Unable to identify the signed-in user. Please try logging in again.");
          return;
        }

        if (profile.role === "developer" && isAdminView) {
          setError("Open your own dashboard from the developer home.");
          return;
        }

        if (profile?.avatar && profile.avatar !== "Profile") {
          setUserAvatar(profile.avatar);
        } else {
          setUserAvatar(profileImg);
        }

        if (!isAdminView) {
          const credits = Number(profile?.creditsEarned);
          setCreditsBalance(Number.isFinite(credits) ? credits : 0);
        }

        const userId = isAdminView ? routeDeveloperId : (profile?._id || getCurrentUserId());

        if (!userId) {
          setError("Unable to identify developer. Please try logging in again.");
          return;
        }

        setDeveloperId(userId);

        const [dashboardResponse, gamesResponse] = await Promise.all([
          getDeveloperDashboard(userId),
          getDeveloperGames(userId),
        ]);

        if (dashboardResponse) {
          setDashboardData(dashboardResponse);
          if (isAdminView) {
            setViewingAs(dashboardResponse.studio?.name || `studio ${userId}`);
            const studioCredits = Number(dashboardResponse.studio?.creditsEarned);
            if (Number.isFinite(studioCredits)) setCreditsBalance(studioCredits);
          }
        }
        if (gamesResponse) {
          setGames(gamesResponse);
        }
      } catch (err) {
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdminView, routeDeveloperId]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("name");
    localStorage.removeItem("username");
    localStorage.removeItem("immerselyEmail");
    localStorage.removeItem("userId");
    localStorage.removeItem("userData");
    navigate("/");
  };

  const handleCreateGame = () => {
    setShowCreateModal(true);
  };

  const handleGameCreated = (newGame) => {
    setGames([newGame, ...games]);
    setShowCreateModal(false);
    if (developerId) {
      getDeveloperDashboard(developerId).then((data) => {
        if (data) {
          setDashboardData(data);
          if (isAdminView) {
            const studioCredits = Number(data.studio?.creditsEarned);
            if (Number.isFinite(studioCredits)) setCreditsBalance(studioCredits);
          }
        }
      });
    }
    if (!isAdminView) {
      getCurrentUserProfile().then((profile) => {
        const credits = Number(profile?.creditsEarned);
        if (Number.isFinite(credits)) setCreditsBalance(credits);
      });
    }
  };

  const basePath = isAdminView ? `/admin/studio/${developerId}` : "/developer";

  const stats = useMemo(
    () => computeDeveloperStats(dashboardData, games),
    [dashboardData, games]
  );

  if (loading) {
    return (
      <div className="dev-loading">
        <div className="dev-spinner" />
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dev-error">
        <div className="dev-error-title">Error</div>
        <p>{error}</p>
        <button type="button" className="dev-btn dev-btn--primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <DeveloperLayout
        userAvatar={userAvatar}
        creditsBalance={creditsBalance}
        onCreateGame={handleCreateGame}
        onLogout={handleLogout}
        basePath={basePath}
        viewingAs={isAdminView ? viewingAs : null}
      >
        <Routes>
          <Route
            index
            element={<DeveloperOverview stats={stats} games={games} basePath={basePath} />}
          />
          <Route
            path="games"
            element={
              <div className="dev-card">
                <div className="dev-card-body" style={{ padding: 0 }}>
                  <GameList
                    games={games}
                    variant="table"
                    showToolbar
                    showCreateButton
                    onCreateClick={handleCreateGame}
                    basePath={basePath}
                  />
                </div>
              </div>
            }
          />
          <Route
            path="reports"
            element={<DeveloperReports games={games} developerId={developerId} />}
          />
          <Route path="*" element={<Navigate to={basePath} replace />} />
        </Routes>
      </DeveloperLayout>

      {showCreateModal && developerId && (
        <CreateGameModal
          developerId={developerId}
          initialCredits={creditsBalance}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleGameCreated}
        />
      )}
    </>
  );
}

export default DeveloperDashboard;
